import React, { useContext, useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { withTranslation } from "react-i18next";
import { withRouter } from "react-router";
import { v4 } from "uuid";
import { Link } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";

import AppSettingsContext from "../../../context/AppSettingsContext";
import BadgeBookingStatus from "../../../ui/BadgeBookingStatus";

import { Button, Card, Dimmer, Grid, Icon, Badge, Nav } from "tabler-react";

import LoadMoreOnBottomScroll from "../../../general/LoadMoreOnBottomScroll";
import { GET_ACCOUNT_CLASSES_QUERY, CANCEL_WAITLIST_MUTATION } from "./queries";
import GET_USER_PROFILE from "../../../../queries/system/get_user_profile";

import ShopAccountClassesBase from "./ShopAccountClassesBase";

function ShopAccountClasses({ t, match, history }) {
    const appSettings = useContext(AppSettingsContext);
    const dateFormat = appSettings.dateFormat;
    const timeFormat = appSettings.timeFormatMoment;
    const [viewMode, setViewMode] = useState("upcoming"); // 'upcoming' or 'past'
    const today = moment().format("YYYY-MM-DD");

    const [cancelWaitlist, { loading: cancelLoading }] = useMutation(
        CANCEL_WAITLIST_MUTATION,
    );

    // Chain queries. First query user data and then query class attendance for that user once we have the account Id.
    const {
        loading: loadingUser,
        error: errorUser,
        data: dataUser,
    } = useQuery(GET_USER_PROFILE);

    // Query for upcoming and past classes with date filtering
    const { loading, error, data, fetchMore } = useQuery(
        GET_ACCOUNT_CLASSES_QUERY,
        {
            skip: loadingUser || errorUser || !dataUser,
            variables: {
                account:
                    dataUser && dataUser.user ? dataUser.user.accountId : null,
                dateFrom: viewMode === "upcoming" ? today : null,
                dateTo: viewMode === "upcoming" ? null : today,
            },
            fetchPolicy: "network-only",
        },
    );

    // No separate waitlist query - we're including waitlist data in the main query

    // Handle loading states
    if (loading || loadingUser || !data) {
        return (
            <ShopAccountClassesBase>
                <Dimmer active={true} loader={true} />
            </ShopAccountClassesBase>
        );
    }

    // Handle error states
    if (error || errorUser) {
        return (
            <ShopAccountClassesBase>
                {t("shop.account.classes.error_loading_data")}
            </ShopAccountClassesBase>
        );
    }

    const user = dataUser.user;

    // Handle leave waitlist action
    const handleLeaveWaitlist = async (attendanceId) => {
        try {
            await cancelWaitlist({
                variables: {
                    input: {
                        scheduleItemAttendance: attendanceId,
                    },
                },
            });

            toast.success(t("shop.classes.waitlist.leave_success"), {
                position: toast.POSITION.BOTTOM_RIGHT,
            });

            // Refresh data
            window.location.reload();
        } catch (error) {
            toast.error(t("general.toast_server_error") + " " + error.message, {
                position: toast.POSITION.BOTTOM_RIGHT,
            });
            console.error("Error leaving waitlist:", error);
        }
    };

    // Check for empty lists
    const scheduleItemAttendances = data.scheduleItemAttendances;

    if (!scheduleItemAttendances.edges.length) {
        return (
            <ShopAccountClassesBase accountName={user.fullName}>
                <Nav>
                    <Nav.Item
                        active={viewMode === "upcoming"}
                        onClick={() => setViewMode("upcoming")}
                    >
                        {t("shop.account.classes.upcoming")}
                    </Nav.Item>
                    <Nav.Item
                        active={viewMode === "past"}
                        onClick={() => setViewMode("past")}
                    >
                        {t("shop.account.classes.past")}
                    </Nav.Item>
                </Nav>
                <Grid.Row className="mt-4">
                    <Grid.Col md={12}>
                        <Card>
                            <Card.Body>
                                {viewMode === "upcoming"
                                    ? t(
                                          "shop.account.classes.empty_list_upcoming",
                                      )
                                    : t("shop.account.classes.empty_list_past")}
                            </Card.Body>
                        </Card>
                    </Grid.Col>
                </Grid.Row>
            </ShopAccountClassesBase>
        );
    }

    // Populated list
    return (
        <ShopAccountClassesBase accountName={user.fullName}>
            <Nav>
                <Nav.Item
                    active={viewMode === "upcoming"}
                    onClick={() => setViewMode("upcoming")}
                >
                    {t("shop.account.classes.upcoming")}
                </Nav.Item>
                <Nav.Item
                    active={viewMode === "past"}
                    onClick={() => setViewMode("past")}
                >
                    {t("shop.account.classes.past")}
                </Nav.Item>
            </Nav>

            <h4 className="mt-4">
                {viewMode === "upcoming"
                    ? t("shop.account.classes.upcoming_title")
                    : t("shop.account.classes.past_title")}
            </h4>
            <LoadMoreOnBottomScroll
                pageInfo={data.scheduleItemAttendances.pageInfo}
                onLoadMore={() => {
                    fetchMore({
                        variables: {
                            after: data.scheduleItemAttendances.pageInfo
                                .endCursor,
                            dateFrom: viewMode === "upcoming" ? today : null,
                            dateTo: viewMode === "upcoming" ? null : today,
                        },
                        updateQuery: (previousResult, { fetchMoreResult }) => {
                            const newEdges =
                                fetchMoreResult.scheduleItemAttendances.edges;
                            const pageInfo =
                                fetchMoreResult.scheduleItemAttendances
                                    .pageInfo;

                            return newEdges.length
                                ? {
                                      // Put the new subscriptions at the end of the list and update `pageInfo`
                                      // so we have the new `endCursor` and `hasNextPage` values
                                      scheduleItemAttendances: {
                                          __typename:
                                              previousResult
                                                  .scheduleItemAttendances
                                                  .__typename,
                                          edges: [
                                              ...previousResult
                                                  .scheduleItemAttendances
                                                  .edges,
                                              ...newEdges,
                                          ],
                                          pageInfo,
                                      },
                                  }
                                : previousResult;
                        },
                    });
                }}
            >
                {data.scheduleItemAttendances.edges.map(({ node }) => (
                    <Card key={v4()}>
                        <Card.Body>
                            <Grid.Row>
                                <Grid.Col xs={12} md={10}>
                                    <div className="mb-xs-3">
                                        <h6>
                                            {
                                                node.scheduleItem
                                                    .organizationClasstype.name
                                            }
                                            <span className="float-xs-right">
                                                {" "}
                                                {node.isWaitlisted ? (
                                                    <Badge color="warning">
                                                        {t(
                                                            "shop.classes.waitlist.position",
                                                            {
                                                                position:
                                                                    node.waitlistPosition,
                                                            },
                                                        )}
                                                    </Badge>
                                                ) : (
                                                    <BadgeBookingStatus
                                                        status={
                                                            node.bookingStatus
                                                        }
                                                    />
                                                )}
                                            </span>
                                        </h6>
                                        <Icon name="clock" />{" "}
                                        {moment(node.date).format(dateFormat)}{" "}
                                        <span className="text-muted">
                                            {moment(
                                                node.date +
                                                    " " +
                                                    node.scheduleItem.timeStart,
                                            ).format(timeFormat)}
                                        </span>
                                        <br />
                                        <Icon name="home" />{" "}
                                        {
                                            node.scheduleItem
                                                .organizationLocationRoom
                                                .organizationLocation.name
                                        }{" "}
                                        <span className="text-muted">
                                            {
                                                node.scheduleItem
                                                    .organizationLocationRoom
                                                    .name
                                            }
                                        </span>
                                        {node.isWaitlisted &&
                                            node.joinedWaitlistAt && (
                                                <div className="mt-2 text-muted">
                                                    <small>
                                                        <Icon name="calendar" />{" "}
                                                        {t(
                                                            "shop.account.classes.joined_waitlist",
                                                        )}
                                                        :{" "}
                                                        {moment(
                                                            node.joinedWaitlistAt,
                                                        ).format(dateFormat)}
                                                    </small>
                                                </div>
                                            )}
                                    </div>
                                </Grid.Col>
                                <Grid.Col xs={12} md={2}>
                                    {node.bookingStatus !== "CANCELLED" ? (
                                        <Link
                                            to={`/shop/account/class_info/${node.scheduleItem.id}/${node.date}`}
                                        >
                                            <Button
                                                block
                                                outline
                                                color="info"
                                                size="sm"
                                                className=""
                                            >
                                                {t("general.info")}
                                            </Button>
                                        </Link>
                                    ) : (
                                        ""
                                    )}
                                    {node.isWaitlisted &&
                                    node.canCancelWaitlist ? (
                                        <Button
                                            block
                                            outline
                                            color="secondary"
                                            size="sm"
                                            className="mt-3"
                                            onClick={() =>
                                                handleLeaveWaitlist(node.id)
                                            }
                                            disabled={cancelLoading}
                                        >
                                            {cancelLoading
                                                ? t("general.please_wait")
                                                : t(
                                                      "shop.classes.waitlist.leave",
                                                  )}
                                        </Button>
                                    ) : !node.isWaitlisted &&
                                      node.bookingStatus !== "CANCELLED" &&
                                      node.cancellationPossible ? (
                                        <Link
                                            to={`/shop/account/class_cancel/${node.scheduleItem.id}/${node.date}/${node.id}`}
                                        >
                                            <Button
                                                block
                                                outline
                                                color="warning"
                                                size="sm"
                                                className="mt-3"
                                            >
                                                {t("general.cancel")}
                                            </Button>
                                        </Link>
                                    ) : node.uncancellationPossible ? (
                                        <Link
                                            to={`/shop/account/class_uncancel/${node.scheduleItem.id}/${node.date}/${node.id}`}
                                        >
                                            <Button
                                                block
                                                outline
                                                color="primary"
                                                size="sm"
                                                className="mt-3"
                                            >
                                                {t("general.book")}
                                            </Button>
                                        </Link>
                                    ) : (
                                        ""
                                    )}
                                </Grid.Col>
                            </Grid.Row>
                        </Card.Body>
                    </Card>
                ))}
            </LoadMoreOnBottomScroll>
        </ShopAccountClassesBase>
    );
}

export default withTranslation()(withRouter(ShopAccountClasses));
